import { useToast } from 'components/common/custom-toast/Toast';
import AppContext from 'context/Context';
import { useEffect, useRef, useContext } from 'react';

const useNetworkMonitor = () => {
  const {config: { isDark }} = useContext(AppContext);
  const hasShownWarning = useRef(false);
  const toast = useToast()
  const toastId = useRef(null);

  const detectSlowNetwork = () => {
    if (!('connection' in navigator)) {
      return false;
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    const slowConnections = ['slow-2g', '2g', '3g'];
    if (connection.effectiveType && slowConnections.includes(connection.effectiveType)) {
      return true;
    }
    
    if (connection.downlink !== undefined && connection.downlink < 1) {
      return true;
    }
    
    if (connection.downlink !== undefined && connection.downlink < 1) {
      return true;
    }
    
    if (connection.rtt !== undefined && connection.rtt > 500) {
      return true;
    }
    
    return false;
  };

  const getConnectionDetails = () => {
    if (!('connection' in navigator)) {
      return '';
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const details = [];

    if (connection.effectiveType) {
      details.push(`Type: ${connection.effectiveType.toUpperCase()}`);
    }
    if (connection.downlink !== undefined) {
      details.push(`Speed: ${connection.downlink.toFixed(2)} Mbps`);
    }
    if (connection.rtt !== undefined) {
      details.push(`Latency: ${connection.rtt}ms`);
    }

    return details.length > 0 ? ` (${details.join(', ')})` : '';
  };

  const showSlowNetworkWarning = () => {
    if (hasShownWarning.current && toastId.current) {
      return;
    }

    const connectionDetails = getConnectionDetails();
    const message = `Slow network detected. Some features may be delayed.`;

    toastId.current = toast.warning(message);

    hasShownWarning.current = true;

    setTimeout(() => {
      hasShownWarning.current = false;
      toastId.current = null;
    }, 30000);
  };

  const showNetworkImprovedToast = () => {
    toast.success('Network connection improved!');
  };

  useEffect(() => {
    if (!('connection' in navigator)) {
      console.warn('Network Information API is not supported in this browser');
      return;
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    let wasSlowPreviously = detectSlowNetwork();

    if (wasSlowPreviously) {
      showSlowNetworkWarning();
    }
    const handleConnectionChange = () => {
      const isSlowNow = detectSlowNetwork();

      if (isSlowNow && !wasSlowPreviously) {
        showSlowNetworkWarning();
        wasSlowPreviously = true;
      } else if (!isSlowNow && wasSlowPreviously) {
        showNetworkImprovedToast();
        wasSlowPreviously = false;
      }
    };

    connection.addEventListener('change', handleConnectionChange);

    return () => {
      connection.removeEventListener('change', handleConnectionChange);
    };
  }, []);

  return {
    detectSlowNetwork,
    showSlowNetworkWarning,
  };
};

export default useNetworkMonitor;
